/**
 * Shared formatting utilities for the Reporting module
 * Centralized here to avoid duplication across 10+ files
 */

export type MetricFormat = 'currency' | 'percent' | 'number';

/**
 * Format a number with K/M abbreviations
 * - Hundreds & below: no decimals (500)
 * - Thousands: 1 decimal with K (1.5K)
 * - Millions: 1 decimal with M (1.2M)
 */
export const formatCompactNumber = (value: number): string => {
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    
    if (absValue >= 1000000) {
        return `${sign}${(absValue / 1000000).toFixed(absValue >= 10000000 ? 0 : 1)}M`;
    }
    if (absValue >= 1000) {
        return `${sign}${(absValue / 1000).toFixed(absValue >= 10000 ? 0 : 1)}K`;
    }
    return Math.round(value).toLocaleString();
};

/**
 * Format currency with K/M abbreviations
 * - Hundreds & below: no decimals ($500)
 * - Thousands: 1 decimal with K ($1.5K)
 * - Millions: 1 decimal with M ($1.2M)
 */
export const formatCompactCurrency = (value: number): string => {
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    
    if (absValue >= 1000000) {
        return `${sign}$${(absValue / 1000000).toFixed(absValue >= 10000000 ? 0 : 1)}M`;
    }
    if (absValue >= 1000) {
        return `${sign}$${(absValue / 1000).toFixed(absValue >= 10000 ? 0 : 1)}K`;
    }
    return `${sign}$${Math.round(absValue).toLocaleString()}`;
};

/**
 * Format percentage value
 */
export const formatPercent = (value: number): string => {
    return `${(value || 0).toFixed(1)}%`;
};

/**
 * Format value for chart display (compact - used on axes and bar labels)
 */
export const formatChartValue = (value: number, format: MetricFormat): string => {
    if (value === null || value === undefined || isNaN(value)) return '';
    
    switch (format) {
        case 'currency':
            return formatCompactCurrency(value);
        case 'percent':
            return formatPercent(value);
        case 'number':
            return formatCompactNumber(value);
        default:
            return String(value);
    }
};

/**
 * Format value for tooltips (full precision)
 */
export const formatTooltipValue = (value: number, format: MetricFormat): string => {
    if (value === null || value === undefined || isNaN(value)) return '';
    
    switch (format) {
        case 'currency':
            return new Intl.NumberFormat('en-US', { 
                style: 'currency', 
                currency: 'USD', 
                maximumFractionDigits: 0 
            }).format(value);
        case 'percent':
            return formatPercent(value);
        case 'number':
            return Math.round(value || 0).toLocaleString();
        default:
            return String(value);
    }
};

/**
 * Get formatter function based on format type
 */
export const getFormatter = (format: MetricFormat): (value: number) => string => {
    switch (format) {
        case 'currency':
            return formatCompactCurrency;
        case 'percent':
            return formatPercent;
        default:
            return formatCompactNumber;
    }
};

/**
 * Calculate "nice" tick values for chart axes
 * Ensures symmetrical scaling (e.g., 20K, 40K, 60K)
 * Returns array of specific tick values
 */
export const calculateNiceTicks = (minValue: number, maxValue: number, tickCount: number = 5): number[] => {
    // Basic validation
    if (isNaN(minValue) || isNaN(maxValue)) return [0, 100];
    if (minValue === maxValue) return [0, maxValue || 100];

    // Force start at 0 for positive ranges (most common in this app)
    // If range crosses zero, we might want different logic, but requirement says "minimum of 0"
    const min = Math.min(0, minValue); 
    // Ensure max is at least slightly above min to avoid division by zero
    const max = Math.max(maxValue, min + 0.000001);
    
    const range = max - min;
    // Calculate raw step
    const rawStep = range / (tickCount - 1);
    
    // Calculate "nice" step (1, 2, 5 * 10^n)
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const normalizedStep = rawStep / magnitude;
    
    let niceStep;
    if (normalizedStep <= 1) niceStep = 1;
    else if (normalizedStep <= 2) niceStep = 2;
    else if (normalizedStep <= 5) niceStep = 5;
    else niceStep = 10;
    
    const step = niceStep * magnitude;
    
    // Generate ticks
    const ticks: number[] = [];
    const niceMin = Math.floor(min / step) * step;
    // Extend max to next step boundary
    const niceMax = Math.ceil(max / step) * step;
    
    // Add small epsilon to handle floating point issues
    for (let t = niceMin; t <= niceMax + step / 1000; t += step) {
        ticks.push(t);
    }
    
    // If we only have one tick or no ticks (edge case), return something sensible
    if (ticks.length < 2) return [0, max];

    return ticks;
};

/** Primary brand color for single-series categorical bar charts (one metric, categories on axis). */
export const PRIMARY_CHART_COLOR = '#0071E3';

/** Prior-period comparison bars (Overview, Domain, Invoicing). */
export const PRIOR_COMPARISON_BAR_FILL = '#CBD5E0';
export const PRIOR_COMPARISON_BAR_FILL_OPACITY = 0.9;

// Blue-only progression for multi-series bar charts (compare mode, invoicing dimensions, etc.)
export const BAR_SERIES_COLORS = [
    PRIMARY_CHART_COLOR, '#3D8FEA', '#6BADF2', '#97C5F7', '#C0DCFA',
    '#005BC4', '#00469B', '#003271', '#2DD0C6', '#25AAAA',
];

// Chart color palette — Apple Blue / Arctic Teal lead, then distinct hues
export const CHART_COLORS = [
    '#0071E3', '#2DD0C6', '#3D8FEA', '#6BADF2', '#97C5F7',
    '#005BC4', '#25AAAA', '#7C5CFC', '#F2994A', '#EB5757',
    '#27AE60', '#2D9CDB', '#BB6BD9', '#00469B', '#1C8080',
];

/** Named pie slices when folding the tail into Other (9+ ranked items). */
export const PIE_MAX_NAMED_SLICES = 7;
/** Show every name when the ranked list is this short — don't hide the last one in Other. */
export const PIE_SHOW_ALL_NAMED_UP_TO = 8;

/** Blue/purple alternating palette for pie and donut charts (Overview Part Type, Invoicing donut). */
export const PIE_DONUT_COLORS = [
    PRIMARY_CHART_COLOR, // PartBay blue
    '#805AD5', // Purple 500
    '#63B3ED', // Blue 300
    '#B794F4', // Purple 300
    '#2C5282', // Blue 800
    '#553C9A', // Purple 800
    '#4299E1', // Blue 400
    '#9F7AEA', // Purple 400
];

/** Colors for multi-entity comparison (vendors/locations/etc). Shares the donut palette so the two stay in sync. */
export const MULTI_ENTITY_COLORS = PIE_DONUT_COLORS.slice(0, 5);

// Green palette for automation charts
export const AUTOMATION_COLORS = [
    '#38A169', '#48BB78', '#68D391', '#9AE6B4', '#C6F6D5',
    '#276749', '#2F855A', '#38A169', '#48BB78', '#68D391'
];

// Tooltip styles - consistent styling for all chart tooltips
export const TOOLTIP_STYLE = {
    backgroundColor: 'white',
    border: '1px solid #E2E8F0',
    borderRadius: '8px',
    boxShadow: 'none',
    fontSize: '12px'
};
